import { useCallback, useEffect, useState } from "react";
import { getInitialTheme, persistTheme } from "../utils/uiGlowUp";

export type Theme = "light" | "dark";

/**
 * Light/dark theme for the DAG glow-up. Mirrors the choice onto
 * html[data-bs-theme] (which the scoped CSS reads) and persists it. The initial
 * value comes from the bootstrap in utils/uiGlowUp (saved choice → OS pref).
 */
export function useTheme(): { theme: Theme; toggle: () => void; setTheme: (t: Theme) => void } {
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof document !== "undefined") {
      const current = document.documentElement.getAttribute("data-bs-theme");
      if (current === "light" || current === "dark") return current;
    }
    return getInitialTheme();
  });

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
  }, []);

  const toggle = useCallback(() => {
    setThemeState((prev) => (prev === "dark" ? "light" : "dark"));
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    // briefly suppress transitions so the colour swap snaps cleanly
    root.classList.add("dag-theme-switching");
    root.setAttribute("data-bs-theme", theme);
    root.classList.toggle("dark", theme === "dark");
    persistTheme(theme);
    const id = window.setTimeout(() => root.classList.remove("dag-theme-switching"), 60);
    return () => {
      window.clearTimeout(id);
      root.classList.remove("dag-theme-switching");
    };
  }, [theme]);

  return { theme, toggle, setTheme };
}
